/**
 * Holds the page editor's content DOM.
 *
 * insm holds no reference to the editor, so the element is registered from the outside — it is
 * the same `editorView.dom` element the editor measures for the `editorDomSize` attribute on
 * its INP event.
 *
 * Only the page editor registers itself: an insm session measures a page, and secondary
 * editors on the page (inline comments, nested legacy content extensions) would make the
 * registration ambiguous.
 *
 * The element is held via a `WeakRef` so a destroyed editor's DOM can be collected even if
 * `unregister` is never reached.
 */
export class EditorDomRegistry {
	private registered: WeakRef<HTMLElement> | undefined;

	/**
	 * Registers the page editor's content DOM element (`editorView.dom`).
	 */
	register(editorDom: HTMLElement): void {
		this.registered = new WeakRef(editorDom);
	}

	/**
	 * Unregisters an editor's content DOM element. Expected to be called when the editor is
	 * destroyed. Ignored when a different editor is currently registered, so an editor being
	 * torn down after its replacement registered does not clear the newer one.
	 */
	unregister(editorDom: HTMLElement): void {
		if (this.registered?.deref() === editorDom) {
			this.registered = undefined;
		}
	}

	/**
	 * The registered editor's content DOM, or undefined when no editor is registered or the
	 * registered one is no longer in the document -- ie. an experience without a page editor,
	 * or a session which outlived the editor.
	 */
	get editorDom(): HTMLElement | undefined {
		const editorDom = this.registered?.deref();

		return editorDom?.isConnected ? editorDom : undefined;
	}
}
